import React, { createContext, useEffect, useReducer, useState, useRef } from 'react';

// third-party — modular Firebase SDK (v9+ style). The compat layer was dropped
// because TOTP multi-factor (TotpMultiFactorGenerator) only exists in the
// modular API; behaviour here is otherwise unchanged.
import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  multiFactor,
  getMultiFactorResolver,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  GoogleAuthProvider,
  GithubAuthProvider,
  type Auth,
  type MultiFactorResolver,
} from 'firebase/auth';

// action - state management
import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

// project imports
import Loader from 'components/Loader';
import { AuthProps, FirebaseContextType, FirebaseMfaSignInResult } from 'types/auth';

// firebase initialize
const firebaseApp = getApps().length
  ? getApp()
  : initializeApp({
      apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_APP_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_APP_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_APP_FIREBASE_MEASUREMENT_ID
    });

/** The one Auth instance for this app. Exported so the other former-compat
 *  callers (authClient, RoleGuard, the settings dialog) share it. */
export const auth: Auth = getAuth(firebaseApp);

// Set session persistence - clears on browser close (additional layer of protection) - RE-ENABLE FOR PROD
//setPersistence(auth, browserSessionPersistence);   // import from 'firebase/auth'

// ==============================|| SESSION TIMEOUT CONFIG ||============================== //
// Change this value for testing vs production:
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours (production)
// const SESSION_DURATION = 5 * 60 * 1000; // 5 minutes (testing)

const LOGIN_TIMESTAMP_KEY = 'loginTimestamp';

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

// const
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // MFA sign-in state
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaVerificationId, setMfaVerificationId] = useState<string | null>(null);

  // MFA enrollment state
  const [mfaEnrollVerificationId, setMfaEnrollVerificationId] = useState<string | null>(null);

  // Separate RecaptchaVerifier instances
  const signInRecaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const enrollRecaptchaRef = useRef<RecaptchaVerifier | null>(null);

  // Helper: reuse or create RecaptchaVerifier for SIGN-IN MFA
  const getSignInRecaptchaVerifier = () => {
    if (!signInRecaptchaRef.current) {
      console.log('[MFA] creating sign-in RecaptchaVerifier on #recaptcha-container');
      signInRecaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible'
      });
    }
    return signInRecaptchaRef.current;
  };

  // Helper: set login timestamp
  const setLoginTimestamp = () => {
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
    console.log('[Auth] Login timestamp set');
  };

  // Helper: clear login timestamp
  const clearLoginTimestamp = () => {
    localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
    console.log('[Auth] Login timestamp cleared');
  };

  // Helper: notify Slack of login (fire-and-forget, non-blocking)
  const notifyLogin = async (email: string, method: string) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const tokenResult = await user.getIdTokenResult();
      const role = (tokenResult.claims.role as string) || 'user';
      
      await fetch('/api/notify/login', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, method, role })
      });
    } catch (e) {
      // Silent fail - don't block login
      console.warn('[Auth] Slack notification failed', e);
    }
  };

  // Helper: check if session is expired
  const isSessionExpired = (): boolean => {
    const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    
    if (!loginTimestamp) {
      // No timestamp means new login - not expired yet
      return false;
    }
    
    const elapsed = Date.now() - parseInt(loginTimestamp, 10);
    const expired = elapsed > SESSION_DURATION;
    
    if (expired) {
      console.log('[Auth] Session expired after', Math.round(elapsed / 1000 / 60), 'minutes');
    }
    
    return expired;
  };

  useEffect(
    () =>
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          // Check if session has expired
          if (isSessionExpired()) {
            console.log('[Auth] Session expired, forcing logout');
            clearLoginTimestamp();
            await signOut(auth);
            return;
          }

          // fetch claims to get role
          const tokenResult = await user.getIdTokenResult();
          const role = (tokenResult.claims.role as string) || '';

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: {
                id: user.uid,
                email: user.email!,
                name: user.email!,
                role
              }
            }
          });
        } else {
          clearLoginTimestamp();
          dispatch({
            type: LOGOUT
          });
        }
      }),
    [dispatch]
  );

  /**
   * Email + password sign-in WITH MFA support.
   */
  const firebaseEmailPasswordSignIn = async (
    email: string,
    password: string
  ): Promise<FirebaseMfaSignInResult> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // No MFA required - set login timestamp
      setLoginTimestamp();
      // Notify Slack (non-blocking)
      notifyLogin(email, 'password');
      return { status: 'ok', userCredential };
    } catch (error: any) {
      console.error('[MFA] signIn error', error);

      if (error.code === 'auth/multi-factor-auth-required') {
        // Modular API: the resolver is derived from the error, not carried on it.
        const resolver = getMultiFactorResolver(auth, error);
        const selectedIndex = 0;

        const recaptchaVerifier = getSignInRecaptchaVerifier();

        const phoneInfoOptions = {
          multiFactorHint: resolver.hints[selectedIndex],
          session: resolver.session
        };

        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const verificationId = await phoneAuthProvider.verifyPhoneNumber(
          phoneInfoOptions,
          recaptchaVerifier
        );

        setMfaResolver(resolver);
        setMfaVerificationId(verificationId);

        return { status: 'mfa-required' };
      }

      // Any other error (wrong password, user-not-found, etc.)
      throw error;
    }
  };

  /**
   * Second step of MFA: verify the SMS code and complete sign-in.
   */
  const firebaseVerifyMfaCode = async (verificationCode: string) => {
    if (!mfaResolver || !mfaVerificationId) {
      throw new Error('No MFA sign-in in progress');
    }

    const cred = PhoneAuthProvider.credential(mfaVerificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

    // This completes the sign-in and will fire onAuthStateChanged
    const userCredential = await mfaResolver.resolveSignIn(multiFactorAssertion);

    // Set login timestamp after successful MFA verification
    setLoginTimestamp();
    
    // Notify Slack (non-blocking)
    const email = userCredential.user?.email || 'unknown';
    notifyLogin(email, 'password+mfa');

    // Clean up just the resolver + verification, but keep RecaptchaVerifier alive
    setMfaResolver(null);
    setMfaVerificationId(null);

    return userCredential;
  };

  /**
   * Resend MFA SMS code using the existing resolver.
   */
  const firebaseResendMfaCode = async (): Promise<void> => {
    if (!mfaResolver) {
      throw new Error('No MFA sign-in in progress');
    }

    const recaptchaVerifier = getSignInRecaptchaVerifier();

    const phoneInfoOptions = {
      multiFactorHint: mfaResolver.hints[0],
      session: mfaResolver.session
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      recaptchaVerifier
    );

    // Update verificationId so the next code they enter is checked against the new SMS
    setMfaVerificationId(verificationId);
  };

  /**
   * Start MFA enrollment: send SMS to a phone number.
   */
  const startMfaEnrollment = async (phoneNumber: string): Promise<void> => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No user is logged in');
    }

    if (!user.emailVerified) {
      throw new Error('Email must be verified before enabling 2FA.');
    }

    console.log('[MFA ENROLL] starting enrollment for', phoneNumber);

    // Extra safety: make sure Firebase has a fresh session for this user
    await user.reload();
    await user.getIdToken(true);

    // 🔁 Clear any previous enrollment Recaptcha instance
    if (enrollRecaptchaRef.current) {
      try {
        console.log('[MFA ENROLL] clearing previous enroll RecaptchaVerifier');
        enrollRecaptchaRef.current.clear();
      } catch (e) {
        console.warn('[MFA ENROLL] error clearing previous RecaptchaVerifier', e);
      }
      enrollRecaptchaRef.current = null;
    }

    // Use the container id string for enrollment reCAPTCHA
    const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-enroll-container', {
      size: 'invisible'
    });
    enrollRecaptchaRef.current = recaptchaVerifier;

    // Get a multi-factor session for enrollment
    const mfaSession = await multiFactor(user).getSession();

    const phoneInfoOptions = {
      phoneNumber,
      session: mfaSession
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);

    try {
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifier
      );

      console.log('[MFA ENROLL] got verificationId', verificationId);

      setMfaEnrollVerificationId(verificationId);
    } catch (err) {
      console.error('[MFA ENROLL] verifyPhoneNumber error', err);
      throw err; // bubble up raw Firebase error
    }
  };

  /**
   * Complete MFA enrollment: verify SMS code and enroll the phone factor.
   */
  const finishMfaEnrollment = async (code: string): Promise<void> => {
    const user = auth.currentUser;

    if (!user) {
      throw new Error('No user is logged in');
    }
    if (!mfaEnrollVerificationId) {
      throw new Error('No MFA enrollment in progress');
    }

    console.log('[MFA ENROLL] finishing enrollment with code', code);

    const cred = PhoneAuthProvider.credential(mfaEnrollVerificationId, code);
    const assertion = PhoneMultiFactorGenerator.assertion(cred);

    try {
      await multiFactor(user).enroll(assertion, 'SMS 2FA');
      console.log('[MFA ENROLL] enrollment success');
    } catch (err) {
      console.error('[MFA ENROLL] enroll error', err);
      throw err; // bubble up raw Firebase error
    } finally {
      // cleanup
      setMfaEnrollVerificationId(null);
    }
  };

  const firebaseGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // Set login timestamp for Google sign-in
    setLoginTimestamp();
    // Notify Slack (non-blocking)
    const email = result.user?.email || 'unknown';
    notifyLogin(email, 'google');
    return result;
  };

  const firebaseGithubSignIn = async () => {
    const provider = new GithubAuthProvider();
    const result = await signInWithPopup(auth, provider);
    // Set login timestamp for GitHub sign-in
    setLoginTimestamp();
    return result;
  };

  const firebaseRegister = async (email: string, password: string) =>
    createUserWithEmailAndPassword(auth, email, password);

  const logout = async () => {
    clearLoginTimestamp();
    return signOut(auth);
  };

  // --- Password reset email via Firebase ---
  //const resetPassword = async (email: string) => {
  //  await sendPasswordResetEmail(auth, email);   // import from 'firebase/auth'
  //};
  // --- Password reset email via Email proxy & Mailgun ---
  const resetPassword = async (email: string) => {
    const res = await fetch('/api/auth/send-password-reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    // IMPORTANT: we still want "safe" UX: do not reveal whether the email exists
    // So if backend returns 200/202 always, we just treat it as success.
    if (!res.ok) {
      // You can keep this generic to avoid leaking info
      throw new Error('Unable to send reset email. Please try again later.');
    }
  };

  // --- Email verification via Firebase ---
  //const sendEmailVerificationEmail = async () => {
  //  const user = auth.currentUser;
  //  if (!user) {
  //    throw new Error('No user is logged in');
  //  }

  //  if (user.emailVerified) {
  //    return;
  //  }

  //  await sendEmailVerification(user);   // import from 'firebase/auth'
  //};

  // --- Email verification via Email proxy & Mailgun ---
  const sendEmailVerificationEmail = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('No user is logged in');

    await user.reload();
    if (user.emailVerified) return;

    const token = await user.getIdToken();

    const res = await fetch('/api/auth/send-verification-email', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: '{}'
    });

    if (!res.ok) {
      let msg = 'Failed to send verification email.';
      try {
        const data = await res.json();
        if (data?.error) msg = data.error;
      } catch {}
      throw new Error(msg);
    }
  };

  const updateProfile = () => {};
  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        firebaseRegister,
        firebaseEmailPasswordSignIn,
        firebaseVerifyMfaCode,
        firebaseResendMfaCode,
        startMfaEnrollment,
        finishMfaEnrollment,
        firebaseGoogleSignIn,
        firebaseGithubSignIn,
        logout,
        resetPassword,
        updateProfile,
        sendEmailVerificationEmail
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext;
